/**
 * cdn-domain-verified-bots-tab.tsx — Verified Bots tab for a CDN domain (act-as).
 *
 * Toggle-list of known good bots (Googlebot, Bingbot, etc.) to allow through
 * WAF/bot-management rules. Full PUT replaces the bots list.
 *
 * Endpoints: GET/PUT /api/v3/cdn/domains/:id/verified-bots
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot } from 'lucide-react';
import { Section, SettingRow, ActionBar } from '@vnetwork/ui/layout';
import {
  Card, CardHeader, CardTitle, CardDescription, CardBody,
  Button, Switch, Skeleton, Badge,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { TabError } from '@/pages/customers/detail/tab-states';
import {
  useActAsCDNVerifiedBots,
  actAsCDNSecurityKeys,
  type VerifiedBot,
} from '@/api/act-as-cdn-security';
import { actAsClient } from '@/api/act-as-client';
import { ActAsMutationConfirm } from '../act-as-mutation-confirm';

/** Well-known bots shown when the API returns an empty list. */
const KNOWN_BOTS: VerifiedBot[] = [
  { id: 'googlebot',       name: 'Googlebot',              enabled: true,  description: 'Google Search crawler' },
  { id: 'bingbot',         name: 'Bingbot',                enabled: true,  description: 'Microsoft Bing crawler' },
  { id: 'facebookbot',     name: 'Facebookbot',            enabled: false, description: 'Facebook link preview crawler' },
  { id: 'twitterbot',      name: 'Twitterbot',             enabled: false, description: 'Twitter/X card preview crawler' },
  { id: 'applebot',        name: 'Applebot',               enabled: false, description: 'Apple Siri / Spotlight crawler' },
  { id: 'linkedinbot',     name: 'LinkedInBot',            enabled: false, description: 'LinkedIn link preview crawler' },
  { id: 'slackbot',        name: 'Slackbot',               enabled: false, description: 'Slack unfurling crawler' },
  { id: 'duckduckbot',     name: 'DuckDuckBot',            enabled: false, description: 'DuckDuckGo crawler' },
  { id: 'baiduspider',     name: 'Baiduspider',            enabled: false, description: 'Baidu search crawler' },
  { id: 'yandexbot',       name: 'YandexBot',              enabled: false, description: 'Yandex search crawler' },
  { id: 'uptimerobot',     name: 'UptimeRobot',            enabled: false, description: 'Uptime monitoring service' },
  { id: 'pingdom',         name: 'Pingdom',                enabled: false, description: 'Pingdom monitoring bot' },
];

interface Props { domainId: string }

export function CdnDomainVerifiedBotsTab({ domainId }: Props) {
  const { t } = useTranslation();
  const q = useActAsCDNVerifiedBots(domainId);
  const [bots, setBots] = useState<VerifiedBot[]>([]);
  const [snapshot, setSnapshot] = useState<VerifiedBot[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    const next = q.data?.bots?.length ? q.data.bots : KNOWN_BOTS;
    setBots(next);
    setSnapshot(next);
  }, [q.data]);

  const dirty = JSON.stringify(bots) !== JSON.stringify(snapshot);

  const toggle = (id: string, enabled: boolean) =>
    setBots((prev) => prev.map((b) => (b.id === id ? { ...b, enabled } : b)));

  const enabledCount = bots.filter((b) => b.enabled).length;

  if (q.isLoading) return <Skeleton style={{ height: 300 }} />;
  if (q.isError) {
    return (
      <TabError
        title={t('cdn_domain.verified_bots_tab.load_error', 'Failed to load verified bots')}
        onRetry={() => q.refetch()}
      />
    );
  }

  return (
    <Section
      title={t('cdn_domain.verified_bots_tab.title', 'Verified Bots')}
      description={t(
        'cdn_domain.verified_bots_tab.description',
        'Allow known good bots to bypass WAF and bot-management challenge rules.',
      )}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Card>
          <CardHeader>
            <CardTitle>{t('cdn_domain.verified_bots_tab.section_bots', 'Known Bots')}</CardTitle>
            <CardDescription>
              {t('cdn_domain.verified_bots_tab.section_bots_desc', '{{n}} of {{total}} bots allowed.', {
                n: enabledCount,
                total: bots.length,
              })}
            </CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
            {bots.length === 0 ? (
              <EmptyState
                size="sm"
                icon={<Bot size={20} />}
                title={t('cdn_domain.verified_bots_tab.no_bots', 'No verified bots configured')}
              />
            ) : (
              bots.map((bot) => (
                <SettingRow
                  key={bot.id}
                  title={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                      {bot.name}
                      {bot.enabled && (
                        <Badge tone="success" appearance="subtle" size="sm">
                          {t('cdn_domain.verified_bots_tab.allowed', 'Allowed')}
                        </Badge>
                      )}
                    </span>
                  }
                  description={bot.description}
                  control={
                    <Switch
                      checked={bot.enabled}
                      onCheckedChange={(v) => toggle(bot.id, v)}
                    />
                  }
                />
              ))
            )}
          </CardBody>
        </Card>

        <ActionBar
          end={
            <>
              <Button
                variant="secondary"
                disabled={!dirty}
                onClick={() => setBots(snapshot)}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button disabled={!dirty} onClick={() => setConfirmOpen(true)}>
                {t('cdn_domain.verified_bots_tab.action_save', 'Save')}
              </Button>
            </>
          }
        />
      </div>

      <ActAsMutationConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('cdn_domain.verified_bots_tab.confirm_save_title', 'Save verified bots')}
        summary={t(
          'cdn_domain.verified_bots_tab.save_summary',
          'Update verified bot allowlist — {{n}} bot(s) enabled.',
          { n: enabledCount },
        )}
        queryKeysToInvalidate={[actAsCDNSecurityKeys.verifiedBots(domainId)]}
        mutationFn={() =>
          actAsClient.put(`/cdn/domains/${domainId}/verified-bots`, { bots })
        }
        onSuccess={() => setSnapshot(bots)}
      />
    </Section>
  );
}
